export type ManagementReportCount = {
  label: string;
  count: number;
};

export type ManagementReportRegistration = {
  memberId: string;
  district: string;
  joined: string;
  products: number;
  brands: number;
};

export type ManagementReportData = {
  generatedAt: string;
  generatedFileStamp: string;
  members: number;
  categories: number;
  products: number;
  brands: number;
  districts: number;
  last30Days: number;
  previous30Days: number;
  growthPercent: number | null;
  profilesWithProducts: number;
  profilesWithBrands: number;
  profilesWithMobile: number;
  profilesWithLocation: number;
  verifiedProfiles: number;
  pendingBrandSuggestions: number;
  categoriesRanking: ManagementReportCount[];
  productsRanking: ManagementReportCount[];
  brandsRanking: ManagementReportCount[];
  districtsRanking: ManagementReportCount[];
  recentRegistrations: ManagementReportRegistration[];
};

function pdfText(value: string) {
  return value.normalize("NFKD").replace(/[^\x20-\x7E]/g, "").trim();
}

export async function generateManagementReportPdf(data: ManagementReportData) {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const navy: [number, number, number] = [7, 53, 128];
  const blue: [number, number, number] = [17, 135, 210];
  const green: [number, number, number] = [11, 150, 116];
  const gold: [number, number, number] = [245, 186, 37];
  const coral: [number, number, number] = [236, 105, 81];
  const muted: [number, number, number] = [82, 109, 147];
  const pale: [number, number, number] = [242, 247, 251];
  const line: [number, number, number] = [218, 229, 240];

  const truncate = (value: string, maxWidth: number) => {
    const clean = pdfText(value) || "Not specified";
    if (doc.getTextWidth(clean) <= maxWidth) return clean;
    let shortened = clean;
    while (shortened.length > 1 && doc.getTextWidth(`${shortened}...`) > maxWidth) shortened = shortened.slice(0, -1);
    return `${shortened}...`;
  };

  const drawHeader = (page: number, subtitle: string) => {
    doc.setFillColor(...navy);
    doc.rect(0, 0, pageWidth, 29, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Kitanego Management Report", margin, 12.5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.8);
    doc.text(subtitle, margin, 19.4);
    doc.text(`Generated ${pdfText(data.generatedAt)}`, margin, 24.3);
    doc.text(`Page ${page} of 2`, pageWidth - margin, 24.3, { align: "right" });
  };

  const drawFooter = () => {
    doc.setDrawColor(...line);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.text("Internal management summary. Aggregate information only; direct member identifiers are excluded.", margin, pageHeight - 9);
    doc.text("A4 portrait", pageWidth - margin, pageHeight - 9, { align: "right" });
  };

  const drawRanking = (title: string, rows: ManagementReportCount[], x: number, y: number, width: number, color: [number, number, number], maxRows = 6) => {
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(title, x, y);
    const visible = rows.slice(0, maxRows);
    const highest = Math.max(1, ...visible.map((row) => row.count));
    visible.forEach((row, index) => {
      const rowY = y + 7 + index * 6.1;
      const labelWidth = width * 0.42;
      const trackX = x + labelWidth + 2;
      const trackWidth = width - labelWidth - 14;
      doc.setTextColor(...muted);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7);
      doc.text(truncate(row.label, labelWidth), x, rowY + 1.2);
      doc.setFillColor(...pale);
      doc.roundedRect(trackX, rowY - 1.6, trackWidth, 3, 1.5, 1.5, "F");
      doc.setFillColor(...color);
      doc.roundedRect(trackX, rowY - 1.6, Math.max(1.4, trackWidth * (row.count / highest)), 3, 1.5, 1.5, "F");
      doc.setTextColor(...navy);
      doc.setFont("helvetica", "bold");
      doc.text(row.count.toLocaleString("en-MY"), x + width, rowY + 1.2, { align: "right" });
    });
    if (!visible.length) {
      doc.setTextColor(...muted);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.2);
      doc.text("No records available.", x, y + 8);
    }
  };

  drawHeader(1, "Executive overview and demand intelligence");
  const metrics = [
    ["Registered members", data.members],
    ["Products selected", data.products],
    ["Brands selected", data.brands],
    ["Categories", data.categories],
    ["Districts", data.districts],
    ["New in 30 days", data.last30Days],
  ] as const;
  const metricGap = 3;
  const metricWidth = (contentWidth - metricGap * 2) / 3;
  metrics.forEach(([label, value], index) => {
    const row = Math.floor(index / 3);
    const column = index % 3;
    const x = margin + column * (metricWidth + metricGap);
    const y = 36 + row * 19;
    doc.setFillColor(...pale);
    doc.roundedRect(x, y, metricWidth, 16, 2.8, 2.8, "F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.7);
    doc.text(label, x + 3, y + 5.7);
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(value.toLocaleString("en-MY"), x + 3, y + 12.8);
  });

  doc.setFillColor(data.growthPercent != null && data.growthPercent >= 0 ? 234 : 255, data.growthPercent != null && data.growthPercent >= 0 ? 248 : 241, data.growthPercent != null && data.growthPercent >= 0 ? 242 : 238);
  doc.roundedRect(margin, 79, contentWidth, 20, 3, 3, "F");
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Registration movement", margin + 5, 87);
  doc.setFontSize(15);
  const growthLabel = data.growthPercent == null ? "New baseline" : `${data.growthPercent >= 0 ? "+" : ""}${data.growthPercent.toFixed(1)}%`;
  doc.text(growthLabel, pageWidth - margin - 5, 89.5, { align: "right" });
  doc.setTextColor(...muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.text(`${data.last30Days.toLocaleString("en-MY")} registrations in the latest 30 days vs ${data.previous30Days.toLocaleString("en-MY")} in the preceding period.`, margin + 5, 94.2);

  const columnGap = 10;
  const columnWidth = (contentWidth - columnGap) / 2;
  drawRanking("Leading product categories", data.categoriesRanking, margin, 112, columnWidth, blue);
  drawRanking("Top selected products", data.productsRanking, margin + columnWidth + columnGap, 112, columnWidth, green);
  drawRanking("Top selected brands", data.brandsRanking, margin, 163, columnWidth, gold);
  drawRanking("Members by district", data.districtsRanking, margin + columnWidth + columnGap, 163, columnWidth, coral);

  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Management attention", margin, 216);
  const attentionRows = [
    ["Pending suggested brands", data.pendingBrandSuggestions, data.pendingBrandSuggestions ? "Review and classify" : "No action required"],
    ["Profiles without products", Math.max(0, data.members - data.profilesWithProducts), "Improve onboarding completion"],
    ["Profiles without mobile contact", Math.max(0, data.members - data.profilesWithMobile), "Consider opt-in reminder"],
  ] as const;
  attentionRows.forEach(([label, value, action], index) => {
    const y = 222 + index * 13;
    doc.setFillColor(...pale);
    doc.roundedRect(margin, y, contentWidth, 10, 2, 2, "F");
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.1);
    doc.text(label, margin + 3, y + 6.3);
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.text(value.toLocaleString("en-MY"), margin + 100, y + 6.3, { align: "right" });
    doc.text(action, pageWidth - margin - 3, y + 6.3, { align: "right" });
  });
  drawFooter();

  doc.addPage("a4", "portrait");
  drawHeader(2, "Member readiness, operational coverage and recent activity");
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Member readiness", margin, 39);
  const readiness = [
    ["Products selected", data.profilesWithProducts, blue],
    ["Brands selected", data.profilesWithBrands, gold],
    ["Mobile contact", data.profilesWithMobile, green],
    ["Registration location", data.profilesWithLocation, coral],
    ["Verified profile", data.verifiedProfiles, navy],
  ] as const;
  readiness.forEach(([label, value, color], index) => {
    const y = 47 + index * 11;
    const trackX = margin + 55;
    const trackWidth = contentWidth - 72;
    const percent = data.members ? Math.round((value / data.members) * 100) : 0;
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.2);
    doc.text(label, margin, y + 1.4);
    doc.setFillColor(...pale);
    doc.roundedRect(trackX, y - 1.8, trackWidth, 3.4, 1.7, 1.7, "F");
    doc.setFillColor(...color);
    if (percent > 0) doc.roundedRect(trackX, y - 1.8, Math.max(1.5, trackWidth * (percent / 100)), 3.4, 1.7, 1.7, "F");
    doc.setTextColor(...navy);
    doc.setFont("helvetica", "bold");
    doc.text(`${value.toLocaleString("en-MY")} (${percent}%)`, pageWidth - margin, y + 1.4, { align: "right" });
  });

  const tableY = 112;
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Latest registrations", margin, tableY);
  doc.setFillColor(...navy);
  doc.roundedRect(margin, tableY + 5, contentWidth, 8, 2, 2, "F");
  const columns = [margin + 3, margin + 39, margin + 86, margin + 132, margin + 158];
  ["Member", "District", "Joined", "Products", "Brands"].forEach((header, index) => {
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(6.5);
    doc.text(header, columns[index], tableY + 10.2);
  });
  data.recentRegistrations.slice(0, 10).forEach((row, index) => {
    const y = tableY + 13 + index * 8;
    if (index % 2 === 0) {
      doc.setFillColor(...pale);
      doc.rect(margin, y, contentWidth, 8, "F");
    }
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.8);
    doc.text(truncate(row.memberId, 32), columns[0], y + 5.1);
    doc.text(truncate(row.district, 42), columns[1], y + 5.1);
    doc.text(truncate(row.joined, 40), columns[2], y + 5.1);
    doc.text(row.products.toLocaleString("en-MY"), columns[3], y + 5.1);
    doc.text(row.brands.toLocaleString("en-MY"), columns[4], y + 5.1);
  });
  if (!data.recentRegistrations.length) {
    doc.setTextColor(...muted);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text("No registrations are available.", margin + 3, tableY + 21);
  }

  doc.setFillColor(238, 248, 254);
  doc.roundedRect(margin, 219, contentWidth, 42, 3, 3, "F");
  doc.setTextColor(...navy);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("How to read this report", margin + 5, 228);
  doc.setTextColor(...muted);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.2);
  const note = "Counts represent selections recorded in member profiles at the time the report was generated. Demand rankings show distinct member selection signals, not confirmed purchases. Use the report for planning and prioritisation while reviewing detailed administrator screens before operational decisions.";
  doc.text(doc.splitTextToSize(note, contentWidth - 10), margin + 5, 236, { lineHeightFactor: 1.55 });
  drawFooter();

  doc.save(`kitanego-management-report-${data.generatedFileStamp}.pdf`);
}
